import { existsSync, mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { homedir } from "node:os";
import { dirname, join } from "node:path";

/**
 * Register the remem-mcp MCP server in agent config files.
 *
 * Config locations:
 * - Claude Code: ~/.claude.json → mcpServers.remem-mcp
 * - Devin CLI:   ~/.config/devin/mcp_config.json → mcpServers.remem-mcp
 * - Cursor:      ~/.cursor/mcp.json → mcpServers.remem-mcp
 * - ZCode:       ~/.zcode/cli/config.json → mcp.servers.remem-mcp (nested under "mcp")
 * - Codex CLI:   ~/.codex/config.toml → [mcp_servers.remem-mcp] (TOML, skip if no parser)
 */

const MCP_SERVER_ENTRY = {
  command: "npx",
  args: ["-y", "remem-mcp"],
};

const MCP_SERVER_ENTRY_WITH_GLOBAL = {
  command: "npx",
  args: ["-y", "remem-mcp"],
  env: {
    REMEM_GLOBAL_SESSION_KEY: "global",
  },
};

interface JsonTarget {
  name: string;
  path: string;
  key: string;
  useGlobal?: boolean;
}

const JSON_TARGETS: JsonTarget[] = [
  {
    name: "Claude Code",
    path: join(homedir(), ".claude.json"),
    key: "mcpServers",
  },
  {
    name: "Devin CLI",
    path: join(homedir(), ".config", "devin", "mcp_config.json"),
    key: "mcpServers",
    useGlobal: true,
  },
  {
    name: "Cursor",
    path: join(homedir(), ".cursor", "mcp.json"),
    key: "mcpServers",
  },
];

/** Register the MCP server in ZCode's config (~/.zcode/cli/config.json).
 *
 * ZCode nests servers under `mcp.servers` (not a top-level `mcpServers` key)
 * and its server schema is strict — an unknown key silently drops the server.
 * Only canonical stdio fields are written: type, command, args, env.
 */
function registerZcodeServer(): boolean {
  const configPath = join(homedir(), ".zcode", "cli", "config.json");
  const config = readJsonConfig(configPath);

  const mcp = (config.mcp as Record<string, unknown>) ?? {};
  const servers = (mcp.servers as Record<string, unknown>) ?? {};

  if (servers["remem-mcp"]) {
    return true; // already registered
  }

  servers["remem-mcp"] = MCP_SERVER_ENTRY;
  mcp.servers = servers;
  config.mcp = mcp;

  writeJsonConfig(configPath, config);
  return true;
}

/** Safely read and parse a JSON config file. Returns {} if file doesn't exist or is invalid. */
function readJsonConfig(path: string): Record<string, unknown> {
  if (!existsSync(path)) return {};
  try {
    return JSON.parse(readFileSync(path, "utf-8"));
  } catch {
    return {};
  }
}

/** Write JSON config file, creating directories as needed. */
function writeJsonConfig(path: string, data: unknown): void {
  const dir = dirname(path);
  if (!existsSync(dir)) mkdirSync(dir, { recursive: true });
  writeFileSync(path, `${JSON.stringify(data, null, 2)}\n`, "utf-8");
}

/** Register MCP server in a JSON config file. */
function registerJsonServer(target: JsonTarget): boolean {
  let config: Record<string, unknown> = {};

  if (existsSync(target.path)) {
    try {
      config = JSON.parse(readFileSync(target.path, "utf-8"));
    } catch {
      // Corrupt config — don't touch it
      // silently skip corrupt configs
      return false;
    }
  }

  const servers = (config[target.key] as Record<string, unknown>) || {};
  const entry = target.useGlobal ? MCP_SERVER_ENTRY_WITH_GLOBAL : MCP_SERVER_ENTRY;

  if (servers["remem-mcp"]) {
    return true; // already registered
  }

  servers["remem-mcp"] = entry;
  config[target.key] = servers;

  const dir = dirname(target.path);
  if (!existsSync(dir)) {
    mkdirSync(dir, { recursive: true });
  }

  writeFileSync(target.path, JSON.stringify(config, null, 2) + "\n", "utf-8");
  return true;
}

/** Register the MCP server in all supported agent configs. */
export async function installMcpServer(): Promise<void> {
  const names: string[] = [];
  for (const target of JSON_TARGETS) {
    // Only register if the config file already exists (agent is installed)
    // or the agent's directory exists.
    // Special case: Cursor stores config in ~/.cursor/mcp.json — create it
    // if the Cursor app directory exists but mcp.json doesn't.
    const agentDir = dirname(target.path);
    if (!existsSync(agentDir)) {
      // Check if Cursor app is installed (macOS)
      if (target.name === "Cursor") {
        const cursorApp = "/Applications/Cursor.app";
        const cursorAppHome = join(homedir(), "Library", "Application Support", "Cursor");
        if (existsSync(cursorApp) || existsSync(cursorAppHome)) {
          // Cursor is installed — create the config directory and file
          mkdirSync(agentDir, { recursive: true });
        } else {
          continue;
        }
      } else {
        continue;
      }
    }

    if (registerJsonServer(target)) {
      names.push(target.name);
    }
  }

  // ZCode: config lives at ~/.zcode/cli/config.json under mcp.servers.
  // Register if the ~/.zcode data directory exists (ZCode has been run);
  // registerZcodeServer creates cli/config.json if the desktop app hasn't.
  if (existsSync(join(homedir(), ".zcode"))) {
    if (registerZcodeServer()) {
      names.push("ZCode");
    }
  }

  // Codex uses TOML — check if config exists and append if missing
  const codexConfig = join(homedir(), ".codex", "config.toml");
  if (existsSync(codexConfig)) {
    const content = readFileSync(codexConfig, "utf-8");
    if (content.includes("[mcp_servers.remem-mcp]")) {
      names.push("Codex CLI");
    } else {
      const tomlEntry = `
[mcp_servers.remem-mcp]
command = "npx"
args = ["-y", "remem-mcp"]

[mcp_servers.remem-mcp.env]
REMEM_GLOBAL_SESSION_KEY = "global"
`;
      writeFileSync(codexConfig, content + tomlEntry, "utf-8");
      names.push("Codex CLI");
    }
  }

  console.log(`MCP server: ${names.join(", ")}`);
}
